import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, Paperclip, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, usePageMeta } from "@/components/site/Reveal";
import { CAREER_AREAS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CV_EXTENSIONS = ["pdf", "doc", "docx"];
const CV_MAX_BYTES = 5 * 1024 * 1024;

const initialForm = { name: "", email: "", phone: "", area: "", message: "" };

const PILLARS = [
  {
    title: "Breadth of Craft",
    text: "From hand-woven carpets and leathercraft to fragrances, property and portfolio management — few groups offer work across such distinct disciplines under one legacy.",
  },
  {
    title: "Generational Perspective",
    text: "A near-century of enterprise teaches patience and discipline. We build for the long term, and careers here are treated the same way.",
  },
  {
    title: "Rooted, Yet Reaching",
    text: "Headquartered in Kolkata since 1928, with businesses serving consumers and partners across India — grounded roots, growing ambitions.",
  },
];

export default function Careers() {
  usePageMeta(
    "Careers — Join SKN Group of Companies",
    "Join SKN Group: careers across six divisions — carpets, footwear, fragrances, real estate, consumables and investments. Apply with your CV."
  );

  const [form, setForm] = useState(initialForm);
  const [cvFile, setCvFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const set = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    if (!CV_EXTENSIONS.includes(ext)) {
      setErrors({ ...errors, cv: "CV must be a PDF or DOC/DOCX file." });
      e.target.value = "";
      return;
    }
    if (file.size > CV_MAX_BYTES) {
      setErrors({ ...errors, cv: "CV must be smaller than 5MB." });
      e.target.value = "";
      return;
    }
    setErrors({ ...errors, cv: null });
    setCvFile(file);
  };

  const clearFile = () => {
    setCvFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!form.email.trim()) errs.email = "Please enter your email.";
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Please enter a valid email address.";
    if (!form.area) errs.area = "Please select an area of interest.";
    if (!form.message.trim()) errs.message = "Please enter a message.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("name", form.name.trim());
      data.append("email", form.email.trim());
      data.append("phone", form.phone.trim());
      data.append("area", form.area);
      data.append("message", form.message.trim());
      if (cvFile) data.append("cv", cvFile);
      await axios.post(`${API}/applications`, data);
      setSubmitted(true);
      toast.success("Your application has been received.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page">
      {/* Header */}
      <section className="pt-40 md:pt-48 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-6">Careers</p>
            <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[1.02] max-w-4xl">
              Join a legacy <span className="italic bronze">in the making</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Since 1928, SKN Group has grown across six divisions through
              generations of perseverance. If that sounds like the kind of place
              you'd like to build your career, we'd like to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="ink-section py-20 md:py-28" data-testid="careers-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#F7F5F0]/10">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.12} className="bg-[#0B0F14]">
              <div className="p-8 md:p-10 h-full border-t-2 border-[#B8997A]/50">
                <h3 className="font-display text-2xl font-medium tracking-tight">{p.title}</h3>
                <p className="mt-4 text-sm md:text-base text-[#F7F5F0]/60 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-14">
          <div className="md:col-span-4">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight leading-[1.1]">
                Apply to <span className="italic bronze">SKN Group</span>
              </h2>
              <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
                We don't list individual openings here. Tell us where your
                interests lie — across any of our divisions or the group's head
                office — and attach your CV. Our team reviews every application.
              </p>
            </Reveal>
          </div>

          <div className="md:col-span-8">
            {submitted ? (
              <Reveal>
                <div
                  className="border border-[#B8997A]/50 p-10 md:p-14"
                  data-testid="careers-success-message"
                >
                  <CheckCircle2 size={40} strokeWidth={1.25} className="bronze" />
                  <h2 className="mt-6 font-display text-3xl md:text-4xl font-medium tracking-tight">
                    Thank you. Your application has been received.
                  </h2>
                  <p className="mt-4 text-muted-foreground leading-relaxed">
                    Our team will review your application and reach out if there's a fit.
                  </p>
                  <button
                    onClick={() => {
                      setForm(initialForm);
                      clearFile();
                      setSubmitted(false);
                    }}
                    data-testid="careers-send-another-button"
                    className="mt-8 inline-flex items-center gap-2 border border-foreground/30 px-6 py-3 text-sm hover:border-[#B8997A] hover:text-[#B8997A] transition-colors duration-300"
                  >
                    Submit Another Application
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal delay={0.1}>
                <form onSubmit={handleSubmit} noValidate className="space-y-7" data-testid="careers-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div className="space-y-2">
                      <Label htmlFor="c-name" className="text-xs uppercase tracking-[0.2em]">
                        Name <span className="bronze">*</span>
                      </Label>
                      <Input
                        id="c-name"
                        data-testid="careers-input-name"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your full name"
                        className="h-12 bg-transparent border-foreground/25 focus-visible:ring-[#B8997A]"
                      />
                      {errors.name && (
                        <p className="text-sm text-destructive" data-testid="careers-error-name">{errors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c-email" className="text-xs uppercase tracking-[0.2em]">
                        Email <span className="bronze">*</span>
                      </Label>
                      <Input
                        id="c-email"
                        type="email"
                        data-testid="careers-input-email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@email.com"
                        className="h-12 bg-transparent border-foreground/25 focus-visible:ring-[#B8997A]"
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive" data-testid="careers-error-email">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                    <div className="space-y-2">
                      <Label htmlFor="c-phone" className="text-xs uppercase tracking-[0.2em]">
                        Phone <span className="text-muted-foreground normal-case">(optional)</span>
                      </Label>
                      <Input
                        id="c-phone"
                        data-testid="careers-input-phone"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+91 ..."
                        className="h-12 bg-transparent border-foreground/25 focus-visible:ring-[#B8997A]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em]">
                        Area of Interest <span className="bronze">*</span>
                      </Label>
                      <Select
                        value={form.area}
                        onValueChange={(v) => {
                          setForm({ ...form, area: v });
                          if (errors.area) setErrors({ ...errors, area: null });
                        }}
                      >
                        <SelectTrigger
                          className="h-12 bg-transparent border-foreground/25 focus:ring-[#B8997A]"
                          data-testid="careers-select-area"
                        >
                          <SelectValue placeholder="Division or area" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#F7F5F0]">
                          {CAREER_AREAS.map((s) => (
                            <SelectItem
                              key={s}
                              value={s}
                              data-testid={`careers-area-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            >
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.area && (
                        <p className="text-sm text-destructive" data-testid="careers-error-area">{errors.area}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="c-message" className="text-xs uppercase tracking-[0.2em]">
                      Message <span className="bronze">*</span>
                    </Label>
                    <Textarea
                      id="c-message"
                      data-testid="careers-input-message"
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Tell us about yourself and the kind of role you're looking for..."
                      rows={6}
                      className="bg-transparent border-foreground/25 focus-visible:ring-[#B8997A] resize-none"
                    />
                    {errors.message && (
                      <p className="text-sm text-destructive" data-testid="careers-error-message">{errors.message}</p>
                    )}
                  </div>

                  {/* CV upload */}
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em]">
                      CV <span className="text-muted-foreground normal-case">(optional — PDF or DOC, max 5MB)</span>
                    </Label>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFile}
                      className="hidden"
                      id="cv-upload"
                      data-testid="careers-input-cv"
                    />
                    {cvFile ? (
                      <div className="flex items-center gap-3 border border-[#B8997A]/50 px-4 py-3" data-testid="careers-cv-selected">
                        <FileText size={16} strokeWidth={1.5} className="bronze" />
                        <span className="text-sm flex-1 truncate">{cvFile.name}</span>
                        <button type="button" onClick={clearFile} data-testid="careers-cv-remove" aria-label="Remove CV">
                          <X size={16} strokeWidth={1.5} className="text-muted-foreground hover:text-destructive transition-colors" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="cv-upload"
                        data-testid="careers-cv-trigger"
                        className="flex items-center gap-2 border border-dashed border-foreground/30 px-4 py-3.5 text-sm text-muted-foreground cursor-pointer hover:border-[#B8997A] hover:text-[#B8997A] transition-colors duration-300"
                      >
                        <Paperclip size={15} strokeWidth={1.5} /> Attach your CV
                      </label>
                    )}
                    {errors.cv && (
                      <p className="text-sm text-destructive" data-testid="careers-error-cv">{errors.cv}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="careers-submit-button"
                    className="inline-flex items-center gap-2 bg-foreground text-background px-10 py-4 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors duration-300 disabled:opacity-60"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    {submitting ? "Submitting..." : "Submit Application"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
